import React from "react";

import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import type { BetSelection } from "@/features/betslip/types";

import { AutomationPills } from "@/features/betslip/components/parts/tokens/AutomationPills";
import { AllocationPicker } from "@/features/betslip/components/parts/tokens/AllocationPicker";
import { BonusCashPill } from "@/features/betslip/components/parts/tokens/BonusCashPill";
import { useBonusCashPill } from "@/features/betslip/hooks/useBonusCashPill";
import { useTokenPills } from "@/features/betslip/hooks/useTokenPills";

import * as styles from "@/features/betslip/components/parts/tokens/PromoPills.module.scss";

type PromoPillsProps = {
  outcomeIds: string[];
  isMulti: boolean;
  selection?: BetSelection;
};

export const PromoPills = ({
  outcomeIds,
  isMulti,
  selection,
}: PromoPillsProps) => {
  const isReviewing = useBetslipStore((state) => state.isReviewing);
  const isPlacing = useBetslipStore((state) => state.isPlacing);
  const isReviewingOrPlacing = isReviewing || isPlacing;

  // A card that can't be bet hides its promo and token pills (an already-applied
  // one still shows so it can be turned off). For a multi, any dead leg counts —
  // it can't be placed while a leg is down.
  const isCardInactive = useBetslipStore((state) =>
    outcomeIds.some((id) => !state.bettable[id]),
  );

  const {
    show: showBonusCash,
    isBonusCashApplied,
    toggle,
  } = useBonusCashPill({
    outcomeIds,
    isMulti,
    selection,
    isReviewingOrPlacing,
    isCardInactive,
  });

  const {
    show: showTokens,
    isLoading,
    selectionEventId,
    selectedAllocationId,
    selectedAutomationType,
    sortedAllocations,
    sortedAutomations,
    usedAllocations,
    legCount,
    multiTokenAllocationId,
    pickAllocation,
    deselectAllocation,
  } = useTokenPills({
    outcomeIds,
    isMulti,
    selection,
    isReviewingOrPlacing,
    isCardInactive,
  });

  if (!showBonusCash && !showTokens) return null;

  return (
    <div className={styles.cardPromos} data-testid="card-promos">
      <div className={styles.pillRow}>
        {showBonusCash && (
          <BonusCashPill
            active={isBonusCashApplied}
            onToggle={toggle}
            disabled={isReviewingOrPlacing}
          />
        )}
        {showTokens && (
          <AutomationPills
            isLoading={isLoading}
            isReviewingOrPlacing={isReviewingOrPlacing}
            selectionEventId={selectionEventId}
            selectedAllocationId={selectedAllocationId}
            selectedAutomationType={selectedAutomationType}
            sortedAllocations={sortedAllocations}
            sortedAutomations={sortedAutomations}
            usedAllocations={usedAllocations}
            onSelect={pickAllocation}
            onDeselect={deselectAllocation}
          />
        )}
      </div>

      <div className={styles.expansion}>
        {showTokens && (
          <AllocationPicker
            isMulti={isMulti}
            legCount={legCount}
            outcomeIds={outcomeIds}
            selectedAllocationId={selectedAllocationId}
            multiTokenAllocationId={multiTokenAllocationId}
            selectedAutomationType={selectedAutomationType}
            sortedAllocations={sortedAllocations}
            sortedAutomations={sortedAutomations}
            onPick={pickAllocation}
            onDeselect={deselectAllocation}
          />
        )}
      </div>
    </div>
  );
};
